/**
 * Frase do Extrato de Premiação que diz onde conferir o pré-requisito não cumprido.
 *
 * O galho que a renderiza (`activeItemObj.date_start >= ciclo_2S2025`) é aberto para cima:
 * foi escrito no 2S2025 e desde então recebe todo ciclo novo. Com o nome do ciclo literal
 * na frase, o 1S2026 mandava o jogador conferir o extrato de **2025/2** — caminho que
 * existe na Plataforma BB, então ninguém batia em erro: conferia o semestre errado.
 *
 * Aqui o nome sai do título do ciclo selecionado, então o galho pode continuar aberto sem
 * envelhecer a cada semestre. Os ciclos fechados (1S2025 e anteriores) seguem com os
 * literais que sempre tiveram, nos galhos fechados do `index.tsx`.
 *
 * O `Extrato de Premiação>` sem espaço antes do `>` é o texto de produção e está
 * preservado de propósito: mudar frase voltada ao cliente passa pelo aval dele.
 *
 * O aviso de nível Jogando Junto mora aqui pelo mesmo motivo: galho aberto para cima citando
 * item da IN, que a revisão da IN renumera entre semestres.
 */

import { getNamedDates } from 'services/utils';

const TITULO_CICLO = /^([12])S(\d{4})$/;

type ItemPorCiclo = { desde: number; item: string };

/**
 * Item da IN 403-1 citado no aviso, por ciclo. A numeração é da versão vigente da IN, não do
 * ciclo — mas ela é revista entre semestres: o 2S2025 cita `6.5.8.2.3` e o 1S2026 cita
 * `6.1.8.2.3`. Por isso o item mora numa tabela por ciclo, e não num literal solto no JSX de
 * um galho aberto para cima — foi assim que o rótulo do ciclo envelheceu.
 *
 * Ordem decrescente: vale o primeiro ciclo já alcançado pela data de início selecionada.
 * Ciclo novo com IN revista = uma linha aqui.
 */
const ITEM_IN_POR_CICLO: ItemPorCiclo[] = [
  { desde: getNamedDates().ciclo_1S2026, item: '6.1.8.2.3' },
  { desde: getNamedDates().ciclo_2S2025, item: '6.5.8.2.3' },
];

/**
 * Item da IN 403-1 do aviso de nível Jogando Junto — o nível é requisito do Prêmio Jogando
 * Junto. Mesma regra da tabela acima; o piso é o 2S2024, que é onde o galho aberto começa.
 */
const ITEM_IN_NIVEL_POR_CICLO: ItemPorCiclo[] = [
  { desde: getNamedDates().ciclo_1S2026, item: '6.1.8.2.4.8.3.4' },
  { desde: getNamedDates().ciclo_2S2024, item: '7.1.7.10' },
];

/** Primeiro ciclo já alcançado pela data; antes do piso da tabela, o próprio piso. */
function itemVigente(tabela: ItemPorCiclo[], cicloDateStart?: number): string {
  const ciclo = tabela.find(({ desde }) => (cicloDateStart ?? 0) >= desde);
  return (ciclo ?? tabela[tabela.length - 1]).item;
}

/** `"1S2026"` → `"2026/1"`. Devolve `''` quando o título não é de um ciclo. */
function formatarRotuloCiclo(cicloTitle?: string): string {
  const match = cicloTitle?.match(TITULO_CICLO);
  if (!match) return '';

  const [, semestre, ano] = match;
  return `${ano}/${semestre}`;
}

/**
 * @param cicloTitle `title` do ciclo selecionado (`"1S2026"`). Ausente ou fora do padrão,
 * a frase sai sem o trecho do ciclo — nunca com `undefined` ou espaço duplo no meio.
 */
export function textoOndeConferirPreRequisito(cicloTitle?: string): string {
  const rotulo = formatarRotuloCiclo(cicloTitle);
  const programa = rotulo ? `Joga Junto BB ${rotulo}` : 'Joga Junto BB';

  return `Para verificar qual o pré-requisito não foi cumprido, você pode acessar Plataforma BB > Minha Visão > Extrato de Premiação> ${programa} > Indicadores.`;
}

/**
 * Primeira frase do aviso: qual requisito da IN 403-1 não foi cumprido.
 *
 * @param cicloDateStart `date_start` do ciclo selecionado — a mesma grandeza que abre o galho
 * no `index.tsx`, para o item e o galho nunca discordarem. Antes do 2S2025 (fora do galho),
 * devolve o item do 2S2025, que é o piso da tabela.
 */
export function textoRequisitoNaoAtingido(cicloDateStart?: number): string {
  const item = itemVigente(ITEM_IN_POR_CICLO, cicloDateStart);

  return `Você não atingiu o cumprimento de pelo menos um dos requisitos elencados na IN 403-1 item ${item}.`;
}

/**
 * Aviso do card "Premiação Pontos Jogando Junto (A)" para quem terminou o ciclo sem o nível.
 *
 * @param cicloDateStart `date_start` do ciclo selecionado, a mesma grandeza que abre o galho
 * `>= ciclo_2S2024` no `index.tsx`.
 */
export function textoNivelJogandoJuntoNaoAtingido(cicloDateStart?: number): string {
  const item = itemVigente(ITEM_IN_NIVEL_POR_CICLO, cicloDateStart);

  return `Você não atingiu o nível Jogando Junto (IN 403-1 item ${item})`;
}
